import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Save, 
  Eye, 
  Settings, 
  Trash2, 
  Copy, 
  Move,
  Type,
  List,
  CheckSquare,
  ToggleLeft,
  Hash,
  Mail,
  Phone,
  Calendar,
  Upload,
  Star,
  Sliders,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formsAPI } from '../utils/api';

const FormEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    questions: [],
    settings: {
      allowMultipleSubmissions: false,
      requireAuthentication: false,
      collectEmail: true,
      showProgressBar: true,
      randomizeQuestions: false,
      timeLimit: null,
      maxSubmissions: null,
      customThankYouMessage: '',
      redirectUrl: ''
    },
    isPublic: false,
    allowedUsers: [],
    expiresAt: null
  });
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // Question types configuration (same as FormBuilder)
  const questionTypes = [
    { 
      id: 'text', 
      name: 'Short Text', 
      icon: Type, 
      description: 'Single line text input',
      category: 'text'
    },
    { 
      id: 'textarea', 
      name: 'Long Text', 
      icon: Type, 
      description: 'Multi-line text input',
      category: 'text'
    },
    { 
      id: 'email', 
      name: 'Email', 
      icon: Mail, 
      description: 'Email address input',
      category: 'text'
    },
    { 
      id: 'phone', 
      name: 'Phone', 
      icon: Phone, 
      description: 'Phone number input',
      category: 'text'
    },
    { 
      id: 'number', 
      name: 'Number', 
      icon: Hash, 
      description: 'Numeric input',
      category: 'text'
    },
    { 
      id: 'date', 
      name: 'Date', 
      icon: Calendar, 
      description: 'Date picker',
      category: 'text'
    },
    { 
      id: 'single-choice', 
      name: 'Single Choice', 
      icon: CheckSquare, 
      description: 'Radio buttons - select one',
      category: 'choice'
    },
    { 
      id: 'multiple-choice', 
      name: 'Multiple Choice', 
      icon: List, 
      description: 'Checkboxes - select multiple',
      category: 'choice'
    },
    { 
      id: 'yes-no', 
      name: 'Yes/No', 
      icon: ToggleLeft, 
      description: 'Simple yes or no question',
      category: 'choice'
    },
    { 
      id: 'rating', 
      name: 'Rating', 
      icon: Star, 
      description: 'Star rating scale',
      category: 'rating'
    },
    { 
      id: 'scale', 
      name: 'Scale', 
      icon: Sliders, 
      description: 'Numeric scale (1-10)',
      category: 'rating'
    },
    { 
      id: 'file-upload', 
      name: 'File Upload', 
      icon: Upload, 
      description: 'File attachment',
      category: 'file'
    }
  ];

  // Fetch form data
  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const response = await formsAPI.getForm(id);
        setFormData(response.data.data);
      } catch (error) {
        console.error('Error fetching form:', error);
        toast.error('Failed to fetch form');
        navigate('/forms');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchForm();
    }
  }, [id, navigate]);

  // Add new question
  const addQuestion = (type) => {
    const newQuestion = {
      id: `q${Date.now()}`,
      question: '',
      type,
      required: false,
      options: type === 'single-choice' || type === 'multiple-choice' ? [
        { id: 'opt1', label: 'Option 1', value: 'option1' },
        { id: 'opt2', label: 'Option 2', value: 'option2' }
      ] : [],
      placeholder: '',
      minLength: null,
      maxLength: null,
      min: null,
      max: null,
      step: 1,
      minLabel: '',
      maxLabel: '',
      scaleMin: 1,
      scaleMax: 5,
      fileTypes: [],
      maxFileSize: 10,
      validation: {}
    };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    setCurrentQuestion(newQuestion.id);
  };

  // Update question
  const updateQuestion = (questionId, updates) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, ...updates } : q
      )
    }));
  };

  // Delete question
  const deleteQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
    if (currentQuestion === questionId) {
      setCurrentQuestion(null);
    }
  };

  // Duplicate question
  const duplicateQuestion = (questionId) => {
    const question = formData.questions.find(q => q.id === questionId);
    if (question) {
      const newQuestion = {
        ...question,
        id: `q${Date.now()}`,
        question: `${question.question} (Copy)`
      };
      setFormData(prev => ({
        ...prev,
        questions: [...prev.questions, newQuestion]
      }));
    }
  };

  // Move question
  const moveQuestion = (questionId, direction) => {
    const questions = [...formData.questions];
    const index = questions.findIndex(q => q.id === questionId);
    
    if (direction === 'up' && index > 0) {
      [questions[index], questions[index - 1]] = [questions[index - 1], questions[index]];
    } else if (direction === 'down' && index < questions.length - 1) {
      [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]];
    }
    
    setFormData(prev => ({
      ...prev,
      questions
    }));
  };

  // Add option to choice question
  const addOption = (questionId) => {
    const question = formData.questions.find(q => q.id === questionId);
    if (question) {
      const newOption = {
        id: `opt${Date.now()}`,
        label: `Option ${question.options.length + 1}`,
        value: `option${question.options.length + 1}`
      };
      updateQuestion(questionId, {
        options: [...question.options, newOption]
      });
    }
  };

  // Update option
  const updateOption = (questionId, optionId, updates) => {
    const question = formData.questions.find(q => q.id === questionId);
    if (question) {
      const updatedOptions = question.options.map(opt =>
        opt.id === optionId ? { ...opt, ...updates } : opt
      );
      updateQuestion(questionId, { options: updatedOptions });
    }
  };

  // Delete option
  const deleteOption = (questionId, optionId) => {
    const question = formData.questions.find(q => q.id === questionId);
    if (question) {
      const updatedOptions = question.options.filter(opt => opt.id !== optionId);
      updateQuestion(questionId, { options: updatedOptions });
    }
  };

  // Save form
  const saveForm = async (status = null) => {
    if (!formData.title.trim()) {
      toast.error('Please enter a form title');
      return;
    }

    if (formData.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    // Validate questions
    const invalidQuestions = formData.questions.filter(q => !q.question.trim());
    if (invalidQuestions.length > 0) {
      toast.error('Please fill in all question texts');
      return;
    }

    try {
      setSaving(true);
      const updateData = { ...formData };
      if (status) {
        updateData.status = status;
      }
      
      await formsAPI.updateForm(id, updateData);
      
      toast.success('Form updated successfully');
      if (status) {
        navigate('/forms');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  // Get current question
  const currentQuestionData = formData.questions.find(q => q.id === currentQuestion);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/forms')}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit Form</h1>
                <p className="text-gray-600">{formData.title}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewMode ? 'Edit' : 'Preview'}
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </button>
              <button
                onClick={() => saveForm()}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => saveForm('Active')}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Sidebar - Question Types */}
        {!previewMode && (
          <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Questions</h3>
              
              {/* Form Basic Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Form Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Form Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter form title"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter form description"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Question Types */}
              <div className="space-y-2">
                {['text', 'choice', 'rating', 'file'].map(category => (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">
                      {category} Questions
                    </h4>
                    <div className="space-y-1">
                      {questionTypes
                        .filter(type => type.category === category)
                        .map(type => (
                          <button
                            key={type.id}
                            onClick={() => addQuestion(type.id)}
                            className="w-full flex items-center p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <type.icon className="w-5 h-5 mr-3 text-gray-500" />
                            <div>
                              <div className="font-medium">{type.name}</div>
                              <div className="text-xs text-gray-500">{type.description}</div>
                            </div>
                          </button>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Questions List */}
          {!previewMode && (
            <div className="flex-1 p-6">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Form Questions ({formData.questions.length})
                </h3>

                {formData.questions.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                    <Type className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h4>
                    <p className="text-gray-600">Add questions from the sidebar to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.questions.map((question, index) => (
                      <motion.div
                        key={question.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white rounded-lg border-2 p-4 ${
                          currentQuestion === question.id 
                            ? 'border-primary-500 shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-gray-500">
                                Q{index + 1}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                {questionTypes.find(t => t.id === question.type)?.name}
                              </span>
                              {question.required && (
                                <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded">
                                  Required
                                </span>
                              )}
                            </div>
                            
                            <div className="mb-3">
                              <input
                                type="text"
                                value={question.question}
                                onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                                placeholder="Enter your question"
                                className="w-full text-lg font-medium text-gray-900 border-none focus:ring-0 p-0"
                              />
                            </div>

                            {/* Question-specific fields - Same as FormBuilder */}
                            {question.type === 'text' || question.type === 'textarea' || question.type === 'email' || question.type === 'phone' ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={question.placeholder}
                                  onChange={(e) => updateQuestion(question.id, { placeholder: e.target.value })}
                                  placeholder="Placeholder text (optional)"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                <div className="flex space-x-2">
                                  <input
                                    type="number"
                                    value={question.minLength || ''}
                                    onChange={(e) => updateQuestion(question.id, { minLength: e.target.value ? parseInt(e.target.value) : null })}
                                    placeholder="Min length"
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                  <input
                                    type="number"
                                    value={question.maxLength || ''}
                                    onChange={(e) => updateQuestion(question.id, { maxLength: e.target.value ? parseInt(e.target.value) : null })}
                                    placeholder="Max length"
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                            ) : question.type === 'number' ? (
                              <div className="flex space-x-2">
                                <input
                                  type="number"
                                  value={question.min || ''}
                                  onChange={(e) => updateQuestion(question.id, { min: e.target.value ? parseFloat(e.target.value) : null })}
                                  placeholder="Min value"
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                <input
                                  type="number"
                                  value={question.max || ''}
                                  onChange={(e) => updateQuestion(question.id, { max: e.target.value ? parseFloat(e.target.value) : null })}
                                  placeholder="Max value"
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                <input
                                  type="number"
                                  value={question.step || ''}
                                  onChange={(e) => updateQuestion(question.id, { step: e.target.value ? parseFloat(e.target.value) : 1 })}
                                  placeholder="Step"
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            ) : (question.type === 'single-choice' || question.type === 'multiple-choice') ? (
                              <div className="space-y-2">
                                {question.options.map((option, optIndex) => (
                                  <div key={option.id} className="flex items-center space-x-2">
                                    <input
                                      type={question.type === 'single-choice' ? 'radio' : 'checkbox'}
                                      disabled
                                      className="w-4 h-4"
                                    />
                                    <input
                                      type="text"
                                      value={option.label}
                                      onChange={(e) => updateOption(question.id, option.id, { label: e.target.value })}
                                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                    <button
                                      onClick={() => deleteOption(question.id, option.id)}
                                      className="p-1 text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => addOption(question.id)}
                                  className="text-sm text-primary-600 hover:text-primary-700"
                                >
                                  + Add Option
                                </button>
                              </div>
                            ) : question.type === 'rating' || question.type === 'scale' ? (
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={question.scaleMin}
                                    onChange={(e) => updateQuestion(question.id, { scaleMin: parseInt(e.target.value) })}
                                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                  <span>to</span>
                                  <input
                                    type="number"
                                    value={question.scaleMax}
                                    onChange={(e) => updateQuestion(question.id, { scaleMax: parseInt(e.target.value) })}
                                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    value={question.minLabel}
                                    onChange={(e) => updateQuestion(question.id, { minLabel: e.target.value })}
                                    placeholder="Min label (optional)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                  <input
                                    type="text"
                                    value={question.maxLabel}
                                    onChange={(e) => updateQuestion(question.id, { maxLabel: e.target.value })}
                                    placeholder="Max label (optional)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                            ) : question.type === 'file-upload' ? (
                              <div className="space-y-2">
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    value={question.fileTypes.join(', ')}
                                    onChange={(e) => updateQuestion(question.id, { fileTypes: e.target.value.split(',').map(t => t.trim()) })}
                                    placeholder="Allowed file types (e.g., pdf, doc, jpg)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                  <input
                                    type="number"
                                    value={question.maxFileSize}
                                    onChange={(e) => updateQuestion(question.id, { maxFileSize: parseInt(e.target.value) })}
                                    placeholder="Max size (MB)"
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                            ) : null}

                            {/* Question Actions */}
                            <div className="flex items-center justify-between mt-4">
                              <div className="flex items-center space-x-2">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={question.required}
                                    onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">Required</span>
                                </label>
                              </div>
                              
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => moveQuestion(question.id, 'up')}
                                  disabled={index === 0}
                                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => moveQuestion(question.id, 'down')}
                                  disabled={index === formData.questions.length - 1}
                                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => duplicateQuestion(question.id)}
                                  className="p-1 text-gray-500 hover:text-gray-700"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteQuestion(question.id)}
                                  className="p-1 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Preview Mode - Same as FormBuilder */}
          {previewMode && (
            <div className="flex-1 p-6">
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {formData.title || 'Untitled Form'}
                  </h2>
                  {formData.description && (
                    <p className="text-gray-600 mb-8">{formData.description}</p>
                  )}

                  <div className="space-y-6">
                    {formData.questions.map((question, index) => (
                      <div key={question.id} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-900">
                          {question.question}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        
                        {/* Preview fields - Same as FormBuilder */}
                        {question.type === 'text' && (
                          <input
                            type="text"
                            placeholder={question.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        )}
                        
                        {question.type === 'textarea' && (
                          <textarea
                            placeholder={question.placeholder}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        )}
                        
                        {question.type === 'email' && (
                          <input
                            type="email"
                            placeholder={question.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        )}
                        
                        {question.type === 'phone' && (
                          <input
                            type="tel"
                            placeholder={question.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        )}
                        
                        {question.type === 'number' && (
                          <input
                            type="number"
                            min={question.min}
                            max={question.max}
                            step={question.step}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        )}
                        
                        {question.type === 'date' && (
                          <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        )}
                        
                        {question.type === 'single-choice' && (
                          <div className="space-y-2">
                            {question.options.map((option) => (
                              <label key={option.id} className="flex items-center">
                                <input
                                  type="radio"
                                  name={`question-${question.id}`}
                                  value={option.value}
                                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {question.type === 'multiple-choice' && (
                          <div className="space-y-2">
                            {question.options.map((option) => (
                              <label key={option.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  value={option.value}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {question.type === 'yes-no' && (
                          <div className="flex space-x-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={`question-${question.id}`}
                                value="yes"
                                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={`question-${question.id}`}
                                value="no"
                                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">No</span>
                            </label>
                          </div>
                        )}
                        
                        {question.type === 'rating' && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{question.minLabel || question.scaleMin}</span>
                            <div className="flex space-x-1">
                              {Array.from({ length: question.scaleMax - question.scaleMin + 1 }, (_, i) => (
                                <button
                                  key={i}
                                  className="w-8 h-8 text-gray-400 hover:text-yellow-400 focus:outline-none"
                                >
                                  <Star className="w-6 h-6" />
                                </button>
                              ))}
                            </div>
                            <span className="text-sm text-gray-600">{question.maxLabel || question.scaleMax}</span>
                          </div>
                        )}
                        
                        {question.type === 'scale' && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm text-gray-600">
                              <span>{question.minLabel || question.scaleMin}</span>
                              <span>{question.maxLabel || question.scaleMax}</span>
                            </div>
                            <input
                              type="range"
                              min={question.scaleMin}
                              max={question.scaleMax}
                              className="w-full"
                            />
                          </div>
                        )}
                        
                        {question.type === 'file-upload' && (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {question.fileTypes.length > 0 && `Allowed: ${question.fileTypes.join(', ')}`}
                              {question.maxFileSize && ` • Max size: ${question.maxFileSize}MB`}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <button className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors">
                      Submit Form
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Settings Sidebar - Same as FormBuilder */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              className="w-80 bg-white border-l border-gray-200 overflow-y-auto"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Form Settings</h3>
                
                <div className="space-y-6">
                  {/* General Settings */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">General</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.settings.allowMultipleSubmissions}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, allowMultipleSubmissions: e.target.checked }
                          }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Allow multiple submissions</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.settings.requireAuthentication}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, requireAuthentication: e.target.checked }
                          }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Require authentication</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.settings.collectEmail}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, collectEmail: e.target.checked }
                          }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Collect email addresses</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.settings.showProgressBar}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, showProgressBar: e.target.checked }
                          }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show progress bar</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.settings.randomizeQuestions}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, randomizeQuestions: e.target.checked }
                          }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Randomize question order</span>
                      </label>
                    </div>
                  </div>

                  {/* Limits */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Limits</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Time Limit (minutes)
                        </label>
                        <input
                          type="number"
                          value={formData.settings.timeLimit || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, timeLimit: e.target.value ? parseInt(e.target.value) : null }
                          }))}
                          placeholder="No limit"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Submissions
                        </label>
                        <input
                          type="number"
                          value={formData.settings.maxSubmissions || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, maxSubmissions: e.target.value ? parseInt(e.target.value) : null }
                          }))}
                          placeholder="No limit"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Messages</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Thank You Message
                        </label>
                        <textarea
                          value={formData.settings.customThankYouMessage}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, customThankYouMessage: e.target.value }
                          }))}
                          placeholder="Thank you for your submission!"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Redirect URL
                        </label>
                        <input
                          type="url"
                          value={formData.settings.redirectUrl}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: { ...prev.settings, redirectUrl: e.target.value }
                          }))}
                          placeholder="https://example.com/thank-you"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Visibility */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Visibility</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isPublic}
                          onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Make form public</span>
                      </label>
                    </div>
                  </div>

                  {/* Expiry */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Expiry</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Date
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.expiresAt ? new Date(formData.expiresAt).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          expiresAt: e.target.value ? new Date(e.target.value) : null
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FormEdit;